import React from 'react';
import { getEntityId } from 'helpers/entityId';
import { Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { CommentType, PIN_COMMENT, UNPIN_COMMENT } from 'services/api/comment';
import HumanReadableTime from 'components/utils/HumanReadableTime';
import { StickyCommentsProps } from './types/types';
import './styles/comments.css';

const StickyComments: React.FC<StickyCommentsProps> = ({
  stickyComments,
  userId,
  isPriority = false,
  textClass
}) => {
  const queryClient = useQueryClient();
  const resolvedTextClass =
    textClass || (isPriority ? 'text-white' : 'text-800');
  const resolvedLinkClass =
    textClass || (isPriority ? 'text-white' : 'text-1000');

  const pinCommentMutation = useMutation(PIN_COMMENT, {
    onSuccess: () => {
      queryClient.invalidateQueries(['stickyComments']);
      toast.success('Comment pinned successfully');
    },
    onError: () => {
      toast.error('Failed to pin comment');
    }
  });

  const unpinCommentMutation = useMutation(UNPIN_COMMENT, {
    onSuccess: () => {
      queryClient.invalidateQueries(['stickyComments']);
      toast.success('Comment unpinned successfully');
    },
    onError: () => {
      toast.error('Failed to unpin comment');
    }
  });

  const handlePinToggle = (commentId: string, isCurrentlyPinned: boolean) => {
    if (isCurrentlyPinned) {
      unpinCommentMutation.mutate(commentId);
    } else {
      pinCommentMutation.mutate(commentId);
    }
  };

  if (stickyComments.length === 0) {
    return null;
  }

  return (
    <div className="mb-3 mt-2">
      <h4 className={classNames('mb-2 fw-bold fs-9', textClass)}>
        Pinned Comments:
      </h4>
      <div
        className="border rounded p-2 sticky-comments-container"
        style={{
          WebkitOverflowScrolling: 'touch',
          maxHeight: '150px',
          overflowY: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        {stickyComments.slice(0, 3).map((comment, index) => (
          <div
            key={getEntityId(comment)}
            className={classNames('mb-2 sticky-comment-row p-2', {
              'border-bottom pb-2':
                index < Math.min(stickyComments.length, 3) - 1
            })}
          >
            <Row className="justify-content-between g-0 align-items-start">
              <Col xs={12} lg={12}>
                <div className="d-flex justify-content-between align-items-start">
                  <p
                    className={classNames(
                      'fs-9 mb-0 flex-grow-1',
                      {
                        'mb-1': comment?.content
                      },
                      resolvedTextClass
                    )}
                  >
                    <Link
                      to={comment?.creator?.username}
                      className={classNames(
                        'fw-semi-bold text-decoration-none',
                        resolvedLinkClass
                      )}
                    >
                      {getEntityId(comment?.creator) === userId
                        ? 'You'
                        : `${comment?.creator?.firstName} ${comment?.creator?.lastName}`}
                    </Link>{' '}
                    <span
                      className={`badge bg-${
                        comment?.commentType === CommentType.Field
                          ? 'success'
                          : 'info'
                      }`}
                    >
                      {comment?.commentType}
                    </span>
                    <span className="badge bg-warning text-dark ms-1">
                      Pinned
                    </span>
                  </p>
                  <FontAwesomeIcon
                    icon={faThumbtack}
                    className="text-warning cursor-pointer ms-2 sticky-comment-pin-icon"
                    title="Unpin comment"
                    onClick={() =>
                      handlePinToggle(getEntityId(comment) || '', true)
                    }
                    style={{ fontSize: '12px' }}
                  />
                </div>
              </Col>
              <Col xs={{ span: 12, order: 1 }} sm={{ span: 'auto', order: 0 }}>
                <p
                  className={classNames(
                    'fw-semi-bold fs-10 mb-0',
                    resolvedTextClass
                  )}
                >
                  <HumanReadableTime
                    date={new Date(comment?.createdAt as unknown as Date)}
                  />
                </p>
              </Col>
              {comment?.content && (
                <Col xs={12} className="mt-1">
                  <p className={classNames('fs-9 mb-0', textClass)}>
                    {comment.content}
                  </p>
                </Col>
              )}
            </Row>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StickyComments;
